import {
  useReactTable,
  ColumnDef,
  getCoreRowModel,
  flexRender,
  PaginationState,
} from '@tanstack/react-table';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SourceUrlItemDto, SourceUrlStatsDto } from '@/Domain/api/Analytics/dto/SourceUrl.dto';
import { CardContent } from '@/Domain/components/Shared/Card/Card';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationButton,
  PaginationNext,
  PaginationPrevious,
} from '@/Domain/components/Shared/Pagination/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/Domain/components/Shared/Table/Table';
import { PaginationHelper } from '@/Domain/helper/Pagination.helper';

type Props = {
  isError: boolean;
  isLoading: boolean;
  data: SourceUrlStatsDto | undefined;
  pagination: PaginationState;
  setPagination: Dispatch<SetStateAction<PaginationState>>;
};

export const SourceUrlsTable = ({ isError, isLoading, data, pagination, setPagination }: Props) => {
  const { t } = useTranslation('insights_page', { keyPrefix: 'ai_insights.source_urls' });

  const totalPages = Math.ceil((data?.count ?? 0) / pagination.pageSize);
  const currentPage = pagination.pageIndex + 1;

  const pages = useMemo(
    () => PaginationHelper.getRange(currentPage, totalPages),
    [currentPage, totalPages],
  );

  const columns: ColumnDef<SourceUrlItemDto>[] = [
    {
      accessorKey: 'url',
      header: () => t('url'),
      enableSorting: false,
      minSize: 350,
      cell: ({ row }) => (
        <a
          href={row.original.full_url}
          target="_blank"
          rel="noopener noreferrer"
          className="truncate inline-block max-w-lg"
          title={row.original.full_url}
        >
          {row.original.url}
        </a>
      ),
    },
    {
      accessorKey: 'count',
      header: () => t('conversations'),
      cell: (info) => info.getValue<number>(),
      enableSorting: false,
      meta: { align: 'right' },
    },
  ];

  const table = useReactTable({
    data: data?.items || [],
    rowCount: data?.count || 0,
    columns,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    state: {
      pagination,
    },
  });

  if (isError) {
    return (
      <CardContent className="pt-0">
        <p className="text-sm text-foreground-subtle text-center py-8">
          {t('error', { defaultValue: 'Failed to load data.' })}
        </p>
      </CardContent>
    );
  }

  if (isLoading) {
    return (
      <CardContent className="pt-0">
        <div className="flex justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-border border-t-foreground" />
        </div>
      </CardContent>
    );
  }

  if (!data?.items.length) {
    return (
      <CardContent className="pt-0">
        <p className="text-sm text-foreground-subtle text-center py-8">
          {t('empty', { defaultValue: 'No source URLs found for this period.' })}
        </p>
      </CardContent>
    );
  }

  return (
    <CardContent className="pt-0">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  <div
                    className={
                      header.column.columnDef.meta?.align === 'right' ? 'flex justify-end' : ''
                    }
                  >
                    <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
                  </div>
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>

        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  style={{ width: cell.column.getSize() || 'auto' }}
                  className={
                    cell.column.columnDef.meta?.align === 'right'
                      ? 'text-right font-medium'
                      : 'text-left'
                  }
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {totalPages > 1 && (
        <div className="flex items-center justify-center pt-4 gap-3">
          <Pagination>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                {t('previous')}
              </PaginationPrevious>
            </PaginationItem>

            <PaginationContent>
              {pages.map((page, idx) =>
                page === '...' ? (
                  <PaginationEllipsis key={`ellipsis-${idx}`}>…</PaginationEllipsis>
                ) : (
                  <PaginationItem
                    key={page}
                    onClick={() => setPagination((old) => ({ ...old, pageIndex: page - 1 }))}
                  >
                    <PaginationButton size="icon" isActive={page === currentPage}>
                      {page}
                    </PaginationButton>
                  </PaginationItem>
                ),
              )}
            </PaginationContent>

            <PaginationItem>
              <PaginationNext onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
                {t('next')}
              </PaginationNext>
            </PaginationItem>
          </Pagination>
        </div>
      )}
    </CardContent>
  );
};
